import React from 'react';

import workspace from "../../../canvas/core/workspace";
import {enableSlabMode} from "../../../canvas/store/canvasHelper";
import store from "../../../../../../store";
import {showSlabTool} from "../../../../../../actions/sqftSlabFunctions";
import {openModalFix} from "../../../../../../actions/sqftModalFunctions";
import SlabConflictWarning from "./SlabConflictWarning";
import {getShapesCenter} from "../../../../helpers/conflictPopupHelper";
import workspaceZoom from "../../../canvas/core/workspaceZoom";
import sqftSlabManager from "../../../canvas/store/sqftSlabManager";
import sqftShapeManager from "../../../canvas/store/sqftShapeManager";
import {MODAL_FIX_CLOSE} from "../../../../../../actions/actionTypes";
import {INTERSECTION_MODE} from "../../../canvas/core/shapes/shapeParts/shapeIntersections";
import {isModalFixEnabled} from "../modalFixConfig";

export const handleOpenSlabModeConflict = async (
  shapeIdsWithConflict,
  {ignoreWizardConflicts = false} = {},
) => {
  if (!isModalFixEnabled()) {
    return;
  }

  const state = store.getState();

  const slabConflictIdsFromStore = state?.sqftSlabState?.sqftSlabConflicts ?? [];
  const slabConflictIds = Array.isArray(shapeIdsWithConflict) && shapeIdsWithConflict.length > 0
    ? shapeIdsWithConflict
    : slabConflictIdsFromStore;

  const {isModalFixOpen} = state.sqftModalFixState;
  const {wizardConflictingSteps} = state.wizardState;
  const intl = workspace.intl;

  if (!ignoreWizardConflicts && wizardConflictingSteps?.length > 0) {
    return;
  }

  if (!slabConflictIds.length) {
    store.dispatch({type: MODAL_FIX_CLOSE});
    return;
  }

  if (isModalFixOpen) {
    return;
  }

  const steps = [];

  steps.push({
    id: 'slabModeConflict',
    title: intl.formatMessage({id: "sqft.popup.slabMode.conflict.title"}),
    content: getSlabConflictContent(slabConflictIds),
    params: {
      slabConflictIds,
    },
    showClose: false,
    showCancel: true,
    showSkip: false,
    showPrev: false,
    isNextButtonDisabled: true,
    nextButtonText: 'Finish',
    enterAnim: 'fadeDown',
    exitAnim: 'fadeUp',
    onStepEnter: (stepData) => {
      const {slabConflictIds} = stepData.params;

      if (slabConflictIds) {
        const shapesWithConflict = workspace.getShapesByIds(slabConflictIds);

        highlightConflictingShapes(shapesWithConflict);

        setTimeout(() => {
          const shapes = shapesWithConflict || [];

          if (shapes.length > 0) {
            const centerPoint = getShapesCenter(shapes);
            workspaceZoom.zoomTo(centerPoint, 0.1);
          }
        }, 500);
      }
    },
    onStepExit: (stepData) => {
      void stepData;
    }
  });

  enableSlabMode();
  store.dispatch(showSlabTool(true));

  store.dispatch(openModalFix('slabModeConflict', steps, () => {
    console.log('slab mode conflicts resolved');
  }));
};

export const findSlabConflictShapes = () => {
  const conflictedShapeMap = new Map();

  const slabPlaceHolderShapes = sqftSlabManager.getSlabPlaceHolderShapes();
  const slabImageShapes = sqftSlabManager.getSlabImageShapes();

  for (const slabShape of [...slabPlaceHolderShapes, ...slabImageShapes]) {
    const overlappedWithShapeIds =
      slabShape?.shapeIntersections?.getOverlappedWithShapeIdsByMode(INTERSECTION_MODE.SLAB) || [];

    if (!overlappedWithShapeIds.length) {
      continue;
    }

    const overlappedShapes = sqftShapeManager.getShapeByIds(overlappedWithShapeIds) || [];

    if (!overlappedShapes.length) {
      continue;
    }

    const slabFeature = slabShape?.getFeature?.();
    const slabSharedFeatureName = slabFeature?.dbFeature?.sharedFeatureName?.toLowerCase() || null;

    if (!slabSharedFeatureName) {
      continue;
    }

    for (const overlappedShape of overlappedShapes) {
      const targetFeature = overlappedShape?.getFeature?.();
      const targetSharedFeatureName = targetFeature?.dbFeature?.sharedFeatureName?.toLowerCase() || null;

      if (!targetSharedFeatureName) {
        continue;
      }

      if (slabSharedFeatureName !== targetSharedFeatureName) {
        conflictedShapeMap.set(overlappedShape.id, overlappedShape);
      }
    }
  }

  return Array.from(conflictedShapeMap.values());
};

const resetAllSlabConflicts = () => {
  const shapes = workspace.getShapesWithoutAttachedResources();

  for (const shape of shapes) {
    shape.isWithConflict = false;
  }
};

export const detectSlabConflicts = () => {
  const shapesWithConflict = findSlabConflictShapes();

  resetAllSlabConflicts();

  for (const shape of shapesWithConflict) {
    shape.isWithConflict = true;
  }

  return shapesWithConflict
    .map(shape => shape?.id)
    .filter(Boolean);
};

const getSlabConflictContent = (conflict) => {
  return React.createElement(SlabConflictWarning, {
    conflict: conflict
  });
};

const highlightConflictingShapes = (shapes) => {
  const normalizedShapes = Array.isArray(shapes)
    ? shapes.filter(Boolean)
    : [];

  for (const shape of normalizedShapes) {
    shape.shapeRender.blink.start({
      blinkMs: 1400,
      totalMs: 5000,
      colorOverride: 'rgba(255,0,0,0.6)'
    });
  }
};
